import { forwardRef, useState, type FC } from 'react'
import * as Sentry from '@sentry/react'
import { Dropdown, Form } from 'react-bootstrap'
import { Button, type ButtonProps } from '../../../components/Button/Button'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import {
  type EmailSubscriptionRequest,
  useCreateEmailSubscriptionMutation,
  useGetEmailSubscriptionsQuery,
  useResubscribeEmailMutation,
  useUnsubscribeEmailMutation,
  useUpdateEmailSubscriptionMutation,
} from '../../../store/emailSubscriptions/emailSubscriptions'
import { type EmailSubscription, type ScheduleCadence } from '../../../models/EmailSubscription'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import styles from './InsightsDigestMenu.module.scss'

type DigestCadence = Extract<ScheduleCadence, 'weekly' | 'biweekly' | 'monthly'>
type DigestChoice = DigestCadence | 'off'

const CADENCE_LABELS: Record<DigestCadence, string> = {
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
}

const DIGEST_CHOICES: { value: DigestChoice; label: string }[] = [
  { value: 'weekly', label: CADENCE_LABELS.weekly },
  { value: 'biweekly', label: CADENCE_LABELS.biweekly },
  { value: 'monthly', label: CADENCE_LABELS.monthly },
  { value: 'off', label: "I don't want to receive digest emails" },
]

const isDigestCadence = (cadence: ScheduleCadence | undefined): cadence is DigestCadence => cadence !== undefined && cadence in CADENCE_LABELS

const currentDigestChoice = (subscription: EmailSubscription | undefined): DigestChoice => {
  const cadence = subscription?.schedule?.scheduleCadence
  if (!subscription || subscription.unsubscribed || !isDigestCadence(cadence)) {
    return 'off'
  }
  return cadence
}

const useSaveDigestChoice = (request: EmailSubscriptionRequest, subscription: EmailSubscription | undefined) => {
  const [createSubscription, { isLoading: isCreating }] = useCreateEmailSubscriptionMutation()
  const [updateSubscription, { isLoading: isUpdating }] = useUpdateEmailSubscriptionMutation()
  const [unsubscribe, { isLoading: isUnsubscribing }] = useUnsubscribeEmailMutation()
  const [resubscribe, { isLoading: isResubscribing }] = useResubscribeEmailMutation()

  const saveCadence = async (cadence: DigestCadence) => {
    if (subscription?.schedule?.scheduleCadence !== cadence) {
      const schedule = { scheduleCadence: cadence }
      const result = subscription?.userId ? await updateSubscription({ ...request, schedule }) : await createSubscription({ ...request, schedule })
      if ('error' in result) {
        return false
      }
    }
    if (subscription?.unsubscribed) {
      const result = await resubscribe(request)
      return !('error' in result)
    }
    return true
  }

  const save = async (choice: DigestChoice) => {
    if (choice === 'off') {
      const result = await unsubscribe(request)
      return !('error' in result)
    }
    return saveCadence(choice)
  }

  return { save, isSaving: isCreating || isUpdating || isUnsubscribing || isResubscribing }
}

const DigestToggle = forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...rest }, ref) => (
  <Button ref={ref} {...rest} type='button' variant='header' className='zocks-gap-0.75 zocks-px-0.75 py-2 button-small'>
    {children}
  </Button>
))
DigestToggle.displayName = 'DigestToggle'

const DigestToggleLabel: FC<{ choice: DigestChoice }> = ({ choice }) =>
  choice === 'off' ? (
    <span className='d-flex align-items-center gap-2'>
      <i className='bi bi-envelope' />
      Get insights in your mailbox?
    </span>
  ) : (
    <span className='d-flex align-items-center gap-2'>
      <i className='bi bi-check2 zocks-text-success' />
      <span>
        <span className='fw-normal'>Receive digest</span> {CADENCE_LABELS[choice]}
      </span>
    </span>
  )

interface DigestChoiceFormProps {
  email: string
  selection: DigestChoice
  canSave: boolean
  isSaving: boolean
  onSelect: (choice: DigestChoice) => void
  onSave: () => void
  onCancel: () => void
}

const DigestChoiceForm: FC<DigestChoiceFormProps> = ({ email, selection, canSave, isSaving, onSelect, onSave, onCancel }) => (
  <div className='d-flex flex-column gap-4 p-3'>
    <p className='m-0 paragraph-small zocks-text-gray-900'>
      Receive insights of what happened in your account to <span className='fw-bold'>{email}.</span>
    </p>
    <div className='d-flex flex-column gap-2'>
      <p className='m-0 paragraph-small zocks-text-gray-900'>How frequently would you like to receive emails?</p>
      <div className='d-flex flex-column zocks-gap-0.125'>
        {DIGEST_CHOICES.map((choice) => (
          <Form.Check
            key={choice.value}
            type='radio'
            id={`insights-digest-${choice.value}`}
            name='insights-digest-cadence'
            className='paragraph-small zocks-text-gray-900'
            label={choice.label}
            checked={selection === choice.value}
            disabled={isSaving}
            onChange={() => onSelect(choice.value)}
          />
        ))}
      </div>
    </div>
    <div className='d-flex gap-2'>
      <Button type='button' variant='primary' className='zocks-px-0.75 py-1' disabled={!canSave} loading={isSaving} onClick={onSave}>
        Save
      </Button>
      <Button type='button' variant='header' className='zocks-px-0.75 py-1' disabled={isSaving} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  </div>
)

interface InsightsDigestPreferencesProps {
  request: EmailSubscriptionRequest
  email: string
}

const InsightsDigestPreferences: FC<InsightsDigestPreferencesProps> = ({ request, email }) => {
  const { data } = useGetEmailSubscriptionsQuery(request)
  const subscription = data?.data.find((item) => item.emailType === request.emailType)
  const { save, isSaving } = useSaveDigestChoice(request, subscription)
  const { trackingClient } = useTracking()

  const [isOpen, setIsOpen] = useState(false)
  const [selection, setSelection] = useState<DigestChoice>('off')

  if (!data) {
    return null
  }

  const currentChoice = currentDigestChoice(subscription)

  const handleToggle = (nextShow: boolean) => {
    if (nextShow) {
      setSelection(currentChoice)
    }
    setIsOpen(nextShow)
  }

  const handleSave = () =>
    Sentry.startSpan({ name: 'insights_digest.save_preferences', op: 'ui.action', attributes: { from: currentChoice, to: selection } }, async () => {
      if (await save(selection)) {
        trackingClient.track({ eventName: TrackEvent.INSIGHTS_DIGEST_SCHEDULE_CHANGED, eventDetails: { from: currentChoice, to: selection } })
        setIsOpen(false)
      }
    })

  return (
    <div className='d-flex justify-content-end align-items-center h-100 px-3'>
      <Dropdown align='end' show={isOpen} autoClose='outside' onToggle={handleToggle}>
        <Dropdown.Toggle as={DigestToggle}>
          <DigestToggleLabel choice={currentChoice} />
          <i className='bi bi-chevron-expand' />
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.menu}>
          <DigestChoiceForm
            email={email}
            selection={selection}
            canSave={selection !== currentChoice}
            isSaving={isSaving}
            onSelect={setSelection}
            onSave={handleSave}
            onCancel={() => handleToggle(false)}
          />
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

export const InsightsDigestMenu: FC = () => {
  const user = useAppSelector(selectAuthenticatedUser)

  if (!user) {
    return null
  }

  const request: EmailSubscriptionRequest = { accountId: user.accountId, userId: user.id, emailType: 'insights_digest' }

  return <InsightsDigestPreferences request={request} email={user.email} />
}
